import { useMemo } from "react";
import { useGetCampaignRoulettesQuery } from "../../../../../services/campaign-roulette";
import { ICampaignRoulette, CampaignType } from "../../../../../@types/campaign-roulette";
import { Meta } from "../../../../../@types/paginated";
import { mapStatusToFrontend } from "../../utils/status-mapper";

interface UseCampaignsDataParams {
  search?: string;
  status?: string;
  orderBy?: string;
  startDate?: string;
  endDate?: string;
  campaignType?: CampaignType;
  meta: {
    page: number;
    perPage?: number;
  };
}

export const useCampaignsData = (params: UseCampaignsDataParams) => {
  const { data: rawData, isLoading, isFetching, error, refetch } = useGetCampaignRoulettesQuery(params);

  const campaignsData = useMemo(() => {
    if (!rawData) return { campaigns: [], meta: {} as Meta };

    const campaigns = rawData.data.map((item: any) => ({
      id: item.id,
      idCapa: item.idCapa,
      nickname: item.campaignName,
      startDate: item.startDate,
      endDate: item.endDate,
      status: mapStatusToFrontend(item.status, item.deletedAt || null),
      campaignType: (item.campaignType || CampaignType.ROULETTE) as CampaignType,
      stores: (item.stores || []).map((store: any) => ({
        storeCode: (store.code?.toString() || store.caixa) as string,
        storeName: (store.name || store.nomeLoja) as string,
        nickname: (store.nickname || store.name || store.nomeLoja) as string,
      })),
      allStoresSelected: item.allStoresSelected || false,
      hasRegulationFile: item.hasRegulationFile || false,
      hasBanner3x4: item.hasBanner3x4 || false,
      hasBanner16x9: item.hasBanner16x9 || false,
      probabilities: (() => {
        const discountProbs = item.discountProbabilities;
        const probArray = Array.isArray(discountProbs) 
          ? discountProbs 
          : (discountProbs?.intervals || []);
        
        return probArray.map((prob: any) => ({
          prob: prob?.prob || 0,
          prizes: prob?.prizes || []
        }));
      })(),
      regulations: item.regulations,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
    } as ICampaignRoulette));

    const meta: Meta = {
      currentPage: rawData.pagination.page,
      lastPage: rawData.pagination.totalPages,
      perPage: rawData.pagination.limit,
      total: rawData.pagination.total,
      prev: rawData.pagination.hasPrevious ? rawData.pagination.page - 1 : null,
      next: rawData.pagination.hasNext ? rawData.pagination.page + 1 : null,
    };

    return { campaigns, meta };
  }, [rawData]);

  return {
    ...campaignsData,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
